/**
 * Stream-aware HTTP-settle signal for snapshot stabilization.
 *
 * Playwright's `waitForLoadState('networkidle')` waits for the underlying
 * connection to **close**. An EventSource (SSE) or WebSocket never closes, so
 * on any app holding such a stream open `networkidle` can never fire — it burns
 * its full timeout on every navigation/interaction, every step.
 *
 * We track requests ourselves (`page.on('request')` → pending,
 * `page.on('response')` → flips off pending when **response headers** arrive).
 * A streaming response sends its headers immediately, so it is marked non-pending
 * right away even though its body never finishes. That makes a pending-count over
 * our own tracker a network-settle signal that is naturally immune to SSE/WS —
 * while still reflecting "the classic XHR/fetch the click fired has landed."
 *
 * This module is the pure counting core, kept browser-free so it can be unit
 * tested by handing it a list of {@link CapturedNetworkRequest}.
 *
 * @module playwright-client/http-settle
 */

import type { CapturedNetworkRequest } from './internal-types.js';

export interface UnsettledOpts {
  /** URL patterns treated as noise (analytics/pixels) and never counted. */
  noisePatterns: readonly RegExp[];
  /** A request still 'pending' past this age (ms) is aged out (ignored). */
  maxAgeMs: number;
}

/** Resource types that are long-lived streams — excluded from the settle count. */
const STREAM_RESOURCE_TYPES = new Set(['eventsource', 'websocket']);

/**
 * Count non-stream HTTP requests that are still awaiting response headers and
 * are younger than the max-age guard. Zero ⇒ "HTTP settled".
 */
export function countUnsettledRequests(
  requests: Iterable<CapturedNetworkRequest>,
  now: number,
  opts: UnsettledOpts
): number {
  let n = 0;
  for (const req of requests) {
    if (req.status !== 'pending') continue;
    // resourceType/startedAt are always set by the live request handler; the
    // undefined guards cover non-production literals (fixtures/formatter). An
    // undefined resourceType is conservatively counted (a brief extra wait is
    // safer than a false-negative that lets an SSE block the settle loop).
    if (req.resourceType !== undefined && STREAM_RESOURCE_TYPES.has(req.resourceType)) continue;
    if (opts.noisePatterns.some((p) => p.test(req.url))) continue;
    if (req.startedAt !== undefined && now - req.startedAt > opts.maxAgeMs) continue;
    n++;
  }
  return n;
}
